import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Logo } from "./Logo";

const links = [
  { to: "/lernwelten", label: "Lernwelten" },
  { to: "/#so-funktioniert", label: "So funktioniert VisualMA", hash: true },
  { to: "/ueber-visualma", label: "Unsere Idee" },
  { to: "/#ringbuch", label: "Ringbuch", hash: true },
  { to: "/faq", label: "FAQ" },
];

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <header
      className="fixed top-0 inset-x-0 z-50 transition-all duration-500"
      style={{
        background: open ? "var(--paper)" : scrolled ? "rgba(251,248,243,0.9)" : "transparent",
        backdropFilter: scrolled && !open ? "blur(16px)" : "none",
        borderBottom: scrolled || open ? "1px solid var(--line)" : "1px solid transparent",
      }}
    >
      <div className="max-w-6xl mx-auto px-5 lg:px-8 h-[68px] flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" aria-label="VisualMA Startseite">
          <Logo />
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {links.map((l) =>
            l.hash ? (
              <a
                key={l.label}
                href={l.to}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className="text-[14px] font-medium text-[color:var(--ink-muted)] hover:text-[color:var(--ink)] transition-colors"
              >
                {l.label}
              </a>
            ) : (
              <NavLink
                key={l.label}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className="text-[14px] font-medium text-[color:var(--ink-muted)] hover:text-[color:var(--ink)] transition-colors"
              >
                {l.label}
              </NavLink>
            )
          )}
          <Link to="/lernwelten" className="btn-primary !py-2.5 !px-6 !text-[14px]" data-testid="nav-cta">
            Lernwelten entdecken
          </Link>
        </nav>

        <button
          className="lg:hidden p-2 -mr-2"
          onClick={() => setOpen(!open)}
          aria-label="Menü öffnen"
          data-testid="nav-mobile-toggle"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="lg:hidden fixed inset-x-0 top-[68px] bottom-0 flex flex-col overflow-y-auto"
            style={{ background: "var(--paper)" }}
            data-testid="nav-mobile-menu"
          >
            <div className="relative flex-1 flex flex-col px-6 pt-4 pb-10 overflow-hidden">
              <div className="blob floaty-slow" style={{ background: "rgba(229,169,60,0.14)", width: 240, height: 225, bottom: "6%", right: "-70px" }} />
              <div className="blob floaty" style={{ background: "rgba(217,83,56,0.1)", width: 190, height: 180, top: "38%", left: "-70px" }} />
              {links.map((l, i) => (
                <motion.div
                  key={l.label}
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.05 + i * 0.05, ease: [0.22, 1, 0.36, 1] }}
                >
                  {l.hash ? (
                    <a href={l.to} onClick={() => setOpen(false)} className="block py-4 font-display text-2xl border-b border-[color:var(--line)]">
                      {l.label}
                    </a>
                  ) : (
                    <Link to={l.to} className="block py-4 font-display text-2xl border-b border-[color:var(--line)]">
                      {l.label}
                    </Link>
                  )}
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.34, ease: [0.22, 1, 0.36, 1] }}
                className="relative mt-auto pt-10"
              >
                <Link to="/lernwelten" className="btn-primary w-full" data-testid="nav-mobile-cta">
                  Lernwelten entdecken
                </Link>
                <p className="font-display italic text-center text-[color:var(--ink-muted)] mt-6">
                  „Lernen darf schön sein."
                </p>
              </motion.div>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
